import { ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Props {
  primaryUrl: string;
  attachedDomains: { hostname: string }[];
}

/**
 * v4 (004-project-overview-tab) US3 — Visit button.
 *
 * 0 or 1 attached custom domains → plain anchor button, no dropdown caret.
 * 2+ attached domains → button + caret revealing a sorted-alphabetical menu
 * of each attached domain. The platform preview URL is NOT in the dropdown
 * when custom domains exist (FR-012 / research §Decision 10).
 */
export function VisitMenu({ primaryUrl, attachedDomains }: Props) {
  // ≤1 attached domain → plain button, no dropdown.
  if (attachedDomains.length < 2) {
    return (
      <Button asChild variant="primary" size="sm">
        <a href={primaryUrl} target="_blank" rel="noreferrer">
          <ExternalLink className="h-3.5 w-3.5" />
          Visit
        </a>
      </Button>
    );
  }

  const sorted = [...attachedDomains]
    .map((d) => d.hostname)
    .sort((a, b) => a.localeCompare(b));

  return (
    <div className="inline-flex">
      <Button asChild variant="primary" size="sm" className="rounded-r-none">
        <a href={`https://${sorted[0]}`} target="_blank" rel="noreferrer">
          <ExternalLink className="h-3.5 w-3.5" />
          Visit
        </a>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="primary"
            size="sm"
            className="rounded-l-none border-l border-black/20 px-2"
            aria-label="Pick a domain to visit"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px]">
          {sorted.map((hostname) => (
            <DropdownMenuItem key={hostname} asChild>
              <a
                href={`https://${hostname}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs cursor-pointer"
              >
                {hostname}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
